// src/pages/ArchiveVentes.js
import React, { useEffect, useState, useMemo } from "react";
import API_URL from "../config";
import { FaSpinner, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import "./SalesManager.css";

export default function ArchiveVentes({ entrepriseId, siteId }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterProduit, setFilterProduit] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterVendeur, setFilterVendeur] = useState("");
  const [filterDenomination, setFilterDenomination] = useState("");

  const [sortField, setSortField] = useState("date_vente");
  const [sortOrder, setSortOrder] = useState("desc");

  const [availableProduits, setAvailableProduits] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableVendeurs, setAvailableVendeurs] = useState([]);
  const [availableDenominations, setAvailableDenominations] = useState([]);

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // -------------------------------
  // FETCH ARCHIVES
  // -------------------------------
  const loadArchives = async () => {
    if (!entrepriseId || !siteId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/archive-ventes/${entrepriseId}/${siteId}`);
      if (!res.ok) throw new Error("Erreur lors du chargement");

      const data = await res.json();

      const enriched = data.map((s) => ({
        ...s,
        produit: s.produit || s.produit_nom || "Inconnu",
        client: s.client || "Inconnu",
        vendeur: s.vendeur || "Inconnu",
        prix_unitaire: Number(s.prix_unitaire) || 0,
        quantite: Number(s.quantite) || 0,
        montant: Number(s.montant) || Number(s.prix_unitaire) * Number(s.quantite) || 0,
        lots_utilises: s.lots_utilises || s.lots || "",
      }));

      setSales(enriched);

      const extractUnique = (fn) => Array.from(new Set(enriched.map(fn).filter(Boolean))).sort();
      setAvailableProduits(extractUnique((s) => s.produit));
      setAvailableClients(extractUnique((s) => s.client));
      setAvailableVendeurs(extractUnique((s) => s.vendeur));

      const denomSet = new Set();
      enriched.forEach((s) => {
        if (!s.lots_utilises) return;
        s.lots_utilises
          .split(",")
          .map((part) => part.trim())
          .forEach((part) => {
            if (!part) return;
            const label = part.replace(/\(.*\)/, "").trim();
            if (label) denomSet.add(label);
          });
      });
      setAvailableDenominations(Array.from(denomSet).sort());
    } catch (err) {
      setError(err.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchives();
  }, [entrepriseId, siteId]);

  // -------------------------------
  // FILTRAGE
  // -------------------------------
  const filteredSales = useMemo(() => {
    return sales.filter((s) => {
      const p = filterProduit ? (s.produit || "").toLowerCase().includes(filterProduit.toLowerCase()) : true;
      const c = filterClient ? (s.client || "").toLowerCase().includes(filterClient.toLowerCase()) : true;
      const v = filterVendeur ? (s.vendeur || "").toLowerCase().includes(filterVendeur.toLowerCase()) : true;

      let dMatch = true;
      if (filterDenomination) {
        const lots = s.lots_utilises || "";
        const pattern = new RegExp("\\b" + escapeRegex(filterDenomination) + "\\s*\\(", "i");
        dMatch = pattern.test(lots);
      }

      return p && c && v && dMatch;
    });
  }, [sales, filterProduit, filterClient, filterVendeur, filterDenomination]);

  // -------------------------------
  // RESTAURATION
  // -------------------------------
  const hasFiltersApplied = filterProduit || filterClient || filterVendeur || filterDenomination;

  const handleRestore = async () => {
    if (filteredSales.length === 0) {
      alert("Aucune donnée filtrée à restaurer.");
      return;
    }

    if (!window.confirm(`Restaurer ${filteredSales.length} ventes dans la table principale ?`)) return;

    // On n'envoie que les IDs de archive_vente au backend
    const ventesPayload = filteredSales.map((s) => ({
      id: s.id,
    }));

    try {
      const res = await fetch(`${API_URL}/api/archive-ventes/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entrepriseId, siteId, ventes: ventesPayload }),
      });

      const json = await res.json();

      if (!res.ok) {
        alert("Erreur restauration: " + (json?.message || res.statusText));
        return;
      }

      alert(
        `Restauration effectuée : ${json.restoredCount} restaurées, ${json.skipped?.length || 0} ignorées, ${json.errors?.length || 0} erreurs.`
      );

      await loadArchives();
      setFilterProduit("");
      setFilterClient("");
      setFilterVendeur("");
      setFilterDenomination("");
    } catch (err) {
      alert("Erreur : " + (err.message || "Erreur inconnue"));
    }
  };

  // -------------------------------
  // TRI
  // -------------------------------
  const sortedSales = useMemo(() => {
    return [...filteredSales].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === "date_vente") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredSales, sortField, sortOrder]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // -------------------------------
  // UI
  // -------------------------------
  if (loading) return <div className="loading"><FaSpinner className="spin" /> Chargement archives...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="archive-container">
      <h2>Archives des ventes</h2>

      {hasFiltersApplied && (
        <button
          className="restore-btn"
          onClick={handleRestore}
          style={{
            marginBottom: "15px",
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "1rem",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#0056b3"}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#007bff"}
        >
          Restaurer ces ventes
        </button>
      )}

      <div className="filters">
        <select value={filterProduit} onChange={(e) => setFilterProduit(e.target.value)}>
          <option value="">Produit</option>
          {availableProduits.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
          <option value="">Client</option>
          {availableClients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={filterVendeur} onChange={(e) => setFilterVendeur(e.target.value)}>
          <option value="">Vendeur</option>
          {availableVendeurs.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select value={filterDenomination} onChange={(e) => setFilterDenomination(e.target.value)}>
          <option value="">Dénomination</option>
          {availableDenominations.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="table-wrapper">
        <table className="sales-table">
          <thead>
            <tr>
              {[
                ["date_vente", "Date"],
                ["produit", "Produit"],
                ["lots_utilises", "Dénomination"],
                ["prix_unitaire", "PU"],
                ["quantite", "Qté"],
                ["montant", "Montant"],
                ["client", "Client"],
                ["vendeur", "Vendeur"],
              ].map(([field, label]) => (
                <th key={field} onClick={() => handleSort(field)}>
                  {label} {sortField === field ? (sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedSales.length === 0 ? (
              <tr><td colSpan={9}>Aucune archive trouvée.</td></tr>
            ) : (
              sortedSales.map((s) => (
                <tr key={s.id}>
                  <td>{s.date_vente ? new Date(s.date_vente).toLocaleDateString() : "—"}</td>
                  <td>{s.produit}</td>
                  <td>{s.lots_utilises || "—"}</td>
                  <td>{Number(s.prix_unitaire).toFixed(2)}</td>
                  <td>{s.quantite}</td>
                  <td>{Number(s.montant).toFixed(2)}</td>
                  <td>{s.client}</td>
                  <td>{s.vendeur}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
